/**
 * Sync Identity Edit → Returning Patient Action
 *
 * Runs on `identifying.onDone` in review/verify mode. Merges the edited
 * identity fields back into:
 *   - `_consumer*` top-level fields (used by the InfoReview screen)
 *   - the cached `_returningPatientOutput` (so a subsequent re-invocation
 *     of the returning-patient arc via `previousOutput` renders InfoReview
 *     with the freshly edited values)
 *
 * Demographics come from the identity arc output (`event.output`).
 * Address comes from intake `responses` keyed by intake question ID and is
 * reverse-mapped via `extractAddressFromResponses`.
 *
 * No-op in the new-patient flow (`_reviewMode` undefined) or when no
 * `_returningPatientOutput` cache exists.
 *
 * The pure `buildIdentityEditSyncPatch` function is exported separately so
 * unit tests can verify the logic without spinning up the full parent
 * machine (see `identity-edit-flow.test.ts`).
 */

import type { ReturningPatientOutput } from "../contracts/arc-contracts";
import { extractAddressFromResponses } from "../helper/address-response-mapper";

/**
 * Pure patch builder for the identity-edit reverse sync.
 *
 * Returns an empty object when:
 *   - not in review/verify mode
 *   - no cached returning-patient output exists
 *   - neither demographics nor address actually changed
 *
 * Otherwise returns a partial context patch with `_consumer*` fields and an
 * updated `_returningPatientOutput` merged on top of the cached output.
 */
export function buildIdentityEditSyncPatch({
  context,
  event,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: XState machine context is widely typed
  context: any;
  // biome-ignore lint/suspicious/noExplicitAny: XState onDone event carries arc output
  event: any;
}): Record<string, unknown> {
  if (!context._reviewMode) return {};

  const rpo = context._returningPatientOutput as
    | ReturningPatientOutput
    | undefined;
  if (!rpo) return {};

  const identityOutput = event.output as
    | Partial<ReturningPatientOutput>
    | undefined;

  const editedAddress = extractAddressFromResponses(
    context.intakeFormSections as
      | Parameters<typeof extractAddressFromResponses>[0]
      | undefined,
    context.responses as Record<string, unknown> | undefined,
  );

  // Build address fields once — reused for both _returningPatientOutput
  // and _consumer* top-level fields with different key names.
  const addrFields: Array<{
    value: string | undefined;
    rpoKey: string;
    consumerKey: string;
  }> = [
    {
      value: editedAddress.street,
      rpoKey: "address",
      consumerKey: "_consumerAddress",
    },
    { value: editedAddress.city, rpoKey: "city", consumerKey: "_consumerCity" },
    {
      value: editedAddress.state,
      rpoKey: "state",
      consumerKey: "_consumerState",
    },
    { value: editedAddress.zip, rpoKey: "zip", consumerKey: "_consumerZip" },
    {
      value: editedAddress.line2,
      rpoKey: "addressLine2",
      consumerKey: "_consumerAddressLine2",
    },
  ];

  const addressPatch: Record<string, string> = {};
  const consumerAddressPatch: Record<string, string> = {};
  for (const { value, rpoKey, consumerKey } of addrFields) {
    if (value !== undefined) {
      addressPatch[rpoKey] = value;
      consumerAddressPatch[consumerKey] = value;
    }
  }

  const demographicsPatch = identityOutput
    ? {
        ...(identityOutput.firstName !== undefined && {
          firstName: identityOutput.firstName,
        }),
        ...(identityOutput.lastName !== undefined && {
          lastName: identityOutput.lastName,
        }),
        ...(identityOutput.dateOfBirth !== undefined && {
          dateOfBirth: identityOutput.dateOfBirth,
        }),
        ...(identityOutput.sex !== undefined && {
          sex: identityOutput.sex,
        }),
        ...(identityOutput.email !== undefined && {
          email: identityOutput.email,
        }),
      }
    : {};

  if (
    Object.keys(addressPatch).length === 0 &&
    Object.keys(demographicsPatch).length === 0
  ) {
    return {};
  }

  return {
    // Keep _consumer* address fields in sync with the cache
    ...consumerAddressPatch,
    // Merge demographics + address into the cached output so a
    // returning-patient re-invocation via `previousOutput` renders
    // infoReview with the freshly edited values.
    _returningPatientOutput: {
      ...rpo,
      ...demographicsPatch,
      ...addressPatch,
    },
  };
}
